import { useState } from 'react'

import styled from 'styled-components'

import BlobIcon from '@totallymoney/ui/components/BlobIcon'
import Box from '@totallymoney/ui/components/Box'
import Button from '@totallymoney/ui/components/Button'
import Divider from '@totallymoney/ui/components/Divider'
import Flex from '@totallymoney/ui/components/Flex'
import Modal from '@totallymoney/ui/components/Modal'
import Row from '@totallymoney/ui/components/Row'
import Text from '@totallymoney/ui/components/Text'
import Icon from '@totallymoney/ui/icons/Icon'
import theme from '@totallymoney/ui/theme'

import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

import {
    CategoryComparatorKey,
    CategorySortKeyComparatorKeys,
} from '@routes/ScoredTableRoute/components/ScoredTable/utils/categorySortKeyComparatorKeys'

const StyledSortByOptionButton = styled.button`
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: ${theme.space10};
    padding: 0;
    background: none;
    border: 0;
    cursor: pointer;
`

interface SortByModalProps {
    initialActiveSortValue: CategoryComparatorKey
    onModalClose: () => void
    onSortChange: (sortKey: CategoryComparatorKey) => void
    showModal: boolean
    sortOptions: Record<
        keyof CategorySortKeyComparatorKeys[CreditCardProductCategory],
        string
    >
}

const SortByModal = ({
    initialActiveSortValue,
    onSortChange,
    sortOptions,
    showModal,
    onModalClose,
}: SortByModalProps) => {
    const [activeSortValue, setActiveSortValue] = useState(
        initialActiveSortValue
    )

    return (
        <Modal
            isFullScreen={true}
            isOpen={showModal}
            onClose={() => {
                setActiveSortValue(initialActiveSortValue)
                onModalClose()
            }}
            title="Sort by"
        >
            <Flex flexDirection="column" justifyContent="center">
                {Object.entries(sortOptions)
                    .reverse()
                    .map(([key, value], index) => (
                        <Box key={key}>
                            {index === 0 && (
                                <Divider color="grey30" spacing="space20" />
                            )}
                            <StyledSortByOptionButton
                                data-testid={`${key}SortByOptionButton`}
                                onClick={() =>
                                    setActiveSortValue(
                                        key as CategoryComparatorKey
                                    )
                                }
                            >
                                {key === activeSortValue ? (
                                    <Icon icon="checkmark" size={16} />
                                ) : (
                                    <Box width={16} height={16} />
                                )}
                                <Text variant="80" color="light" weight="bold">
                                    {value}
                                </Text>
                            </StyledSortByOptionButton>

                            <Divider color="grey30" spacing="space20" />
                        </Box>
                    ))}
                <Flex my="space40">
                    <Row space="space10" alignItems="center">
                        <BlobIcon blobName="lightBulb" />
                        <Text variant="80">
                            <Text variant="80" display="inline" weight="bold">
                                Match Factor
                            </Text>{' '}
                            takes into account the best aspect of each card,
                            combined with your likelihood of being accepted.
                        </Text>
                    </Row>
                </Flex>
                <Flex
                    flexDirection="column"
                    mx="auto"
                    width={['100%', '343px']}
                >
                    <Button
                        variant="primary"
                        text="See results"
                        disabled={initialActiveSortValue === activeSortValue}
                        onClick={() => {
                            onSortChange(activeSortValue)
                            onModalClose()
                        }}
                    />
                </Flex>
            </Flex>
        </Modal>
    )
}

export default SortByModal
